import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { IconName } from '../../../shared/components/icon/icon-data.generated';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { InputComponent } from '../../../shared/components/input/input.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { formatLocalDate, formatLocalTime } from '../../../shared/utils/date-time/date-time.utils';
import { DisclosedRecord, PatientPortalAccessLogDetail } from '../../models/access-log-detail';
import { ActivityLogsService } from '../../services/activity-logs.service';
import { getActorRoleLabel } from '../../utils/actor-role/actor-role.utils';
import { getErrorStatus } from '../../utils/http-error/http-error.utils';

const NOT_FOUND_MESSAGE =
  'This disclosure may have been removed, or the link you followed is no longer valid.';
const LOAD_ERROR_MESSAGE = 'Something went wrong while loading this disclosure. Please try again.';

/** A single disclosed-record row, projected for the table. */
interface DisclosedRecordRow {
  id: number;
  name: string;
  description: string | null;
  status: string | null;
  mostRecent: string;
  isDeleted: boolean;
}

@Component({
  selector: 'app-disclosure-detail',
  standalone: true,
  imports: [FormsModule, RouterLink, IconComponent, InputComponent, ProgressSpinnerComponent],
  templateUrl: './disclosure-detail.html',
  styleUrl: './disclosure-detail.scss',
})
export class DisclosureDetail implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly activityLogsService = inject(ActivityLogsService);

  readonly logId = Number(this.route.snapshot.paramMap.get('logId') ?? 0);

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly notFound = signal(false);
  readonly detail = signal<PatientPortalAccessLogDetail | null>(null);
  readonly searchTerm = signal('');

  readonly errorTitle = computed(() =>
    this.notFound() ? 'Disclosure not found' : 'Unable to load disclosure',
  );
  readonly errorIcon = computed<IconName>(() => (this.notFound() ? 'search' : 'warning'));

  /** Feature area drives the page title and last breadcrumb crumb (e.g. "Test Results"). */
  readonly featureArea = computed(() => this.detail()?.featureArea ?? '');

  /** The portal user who performed the access; shown in the subtitle and breadcrumb. */
  readonly actorName = computed(() => this.detail()?.actingPortalUser?.displayName ?? '');

  readonly accessedOn = computed(() => {
    const detail = this.detail();
    return detail ? formatLocalDate(detail.createdOnUtc) : '';
  });

  /**
   * Subtitle: "Accessed {date} at {time} · {actor} ({relationship})". The relationship label comes
   * from {@link getActorRoleLabel}, shared with the appointment screen so the same event named the
   * same way whichever of the two it opens on.
   */
  readonly subtitle = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return '';
    }
    const when = `Accessed ${formatLocalDate(detail.createdOnUtc)} at ${formatLocalTime(detail.createdOnUtc)}`;
    const actor = detail.actingPortalUser;
    if (!actor) {
      return when;
    }
    return `${when} · ${actor.displayName} (${getActorRoleLabel(actor)})`;
  });

  /** Precompiled explanation shown as a banner for increased-severity disclosures. */
  readonly severityNote = computed(() => this.detail()?.severityNote ?? null);

  private readonly rows = computed<DisclosedRecordRow[]>(
    () => this.detail()?.disclosedRecords.map((record) => this.toRow(record)) ?? [],
  );

  /**
   * Column visibility is data-driven: the Description and Status columns are hidden entirely when
   * no disclosed record carries a value for them (some source tables have neither). Based on the
   * full record set, not the filtered view, so columns don't appear/disappear as you search.
   */
  readonly showDescriptionColumn = computed(() => this.rows().some((row) => !!row.description));
  readonly showStatusColumn = computed(() => this.rows().some((row) => !!row.status));

  /** Rows filtered client-side by the search box (matches name, description, or status). */
  readonly filteredRows = computed<DisclosedRecordRow[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.rows();
    }
    return this.rows().filter((row) =>
      [row.name, row.description, row.status].some((value) => value?.toLowerCase().includes(term)),
    );
  });

  readonly hasActiveSearch = computed(() => this.searchTerm().trim().length > 0);

  ngOnInit(): void {
    if (!Number.isInteger(this.logId) || this.logId <= 0) {
      this.showNotFound();
      return;
    }

    this.activityLogsService
      .getAccessLogById(this.logId)
      .pipe(
        catchError((error: unknown) => {
          // A missing record arrives either as a 404 or as a RecordNotFound envelope (handled
          // below, as a null emission); anything else is a genuine failure.
          if (getErrorStatus(error) === 404) {
            this.showNotFound();
          } else {
            this.notFound.set(false);
            this.loadError.set(LOAD_ERROR_MESSAGE);
            this.isLoading.set(false);
          }
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((detail) => {
        if (!detail) {
          // Either the catchError above already set the state, or the envelope carried a null
          // result for an id that doesn't exist — both read as "not found". Without this the
          // spinner would spin forever on a RecordNotFound envelope.
          if (!this.loadError()) {
            this.showNotFound();
          }
          return;
        }
        this.detail.set(detail);
        this.loadError.set(null);
        this.notFound.set(false);
        this.isLoading.set(false);
      });
  }

  private showNotFound(): void {
    this.notFound.set(true);
    this.loadError.set(NOT_FOUND_MESSAGE);
    this.isLoading.set(false);
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
  }

  private toRow(record: DisclosedRecord): DisclosedRecordRow {
    return {
      id: record.recordId,
      name: record.name,
      description: record.description,
      status: record.statusType,
      mostRecent: formatLocalDate(record.modifiedOnUtc),
      isDeleted: record.isDeleted,
    };
  }
}
